// src/ArchiveTab.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';
import api from '../services/api';
import { motion } from 'framer-motion';

export default function ArchiveTab() {
  const [books, setBooks] = useState([]);
  const [form, setForm] = useState({
    name: '',
    author: '',
    genre: 'عام',
    entryDate: '',
    barcode: '',
    coverUrl: '',   // رابط اختياري
    price: ''       // نص (نحوّله رقم عند الإرسال)
  });

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  // تحكم العرض
  const [filterGenre, setFilterGenre] = useState('');
  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // بحث/فرز
  const [searchTerm, setSearchTerm] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [categorySort, setCategorySort] = useState('alpha');
  const [perCategorySort, setPerCategorySort] = useState({}); // { [genreName]: 'alpha' | 'entryDate' }

  // صورة افتراضية (SVG) إذا ماكو غلاف
  const placeholderCover =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="420">
        <rect width="100%" height="100%" fill="#eef2ff"/>
        <rect x="20" y="20" width="260" height="380" rx="10" fill="#c7d2fe" stroke="#93c5fd" stroke-width="2"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#1e3a8a" font-size="18" font-family="Arial">
          لا توجد صورة غلاف
        </text>
      </svg>`
    );

  useEffect(() => { fetchBooks(); }, []);

  function fetchBooks() {
    api.get('/books')
      .then(res => {
        if (Array.isArray(res.data)) setBooks(res.data);
        else if (Array.isArray(res.data?.books)) setBooks(res.data.books);
        else setBooks([]);
      })
      .catch(() => setBooks([]));
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // ✅ الحد صار 100MB
  const validateFile = (file) => {
    if (!file) return true;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      alert('صيغة الصورة غير مدعومة. الصيغ المسموحة: PNG / JPG / JPEG / WEBP / GIF');
      return false;
    }
    if (file.size > 100 * 1024 * 1024) {
      alert('حجم الصورة أكبر من 100MB. رجاءً اختر صورة أصغر.');
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!validateFile(file)) {
      e.target.value = '';
      return;
    }
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(file);
    setCoverPreview(file ? URL.createObjectURL(file) : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving || isUploading) return;

    setIsSaving(true);
    try {
      let coverUrl = (form.coverUrl || '').trim();

      if (coverFile) {
        setIsUploading(true);
        const fd = new FormData();
        fd.append('cover', coverFile);
        const up = await api.post('/uploads/cover', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          // 👇 للسماح برفع الملفات الكبيرة عبر Axios
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 0,
        });
        coverUrl = up.data.url;
        setIsUploading(false);
      }

      const data = {
        ...form,
        coverUrl,
        price: form.price === '' ? 0 : Number(form.price),
        barcode: form.barcode || `B${Date.now()}`
      };

      if (editId) await api.put(`/books/${editId}`, data);
      else await api.post('/books', data);

      // إعادة ضبط النموذج
      setEditId(null);
      setCoverFile(null);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview('');
      setFileInputKey(Date.now());
      setForm({
        name: '', author: '', genre: 'عام', entryDate: '',
        barcode: '', coverUrl: '', price: ''
      });
      fetchBooks();
    } catch (err) {
      console.error(err);
      alert('❌ خطأ عند رفع/حفظ الكتاب');
    } finally {
      setIsSaving(false);
      setIsUploading(false);
    }
  };

  const handleEdit = (book) => {
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setForm({
      name: book.name || '',
      author: book.author || '',
      genre: book.genre || 'عام',
      entryDate: book.entryDate ? String(book.entryDate).split('T')[0] : '',
      barcode: book.barcode || '',
      coverUrl: book.coverUrl || '',
      price: (book.price ?? '').toString()
    });
    setEditId(book._id);
    setCoverFile(null);
    setCoverPreview(book.coverUrl || '');
    setFileInputKey(Date.now());
  };

  const handleDelete = async (id) => {
    const ok = window.confirm('هل أنت متأكد أنك تريد حذف هذا الكتاب؟');
    if (!ok) return;
    try {
      await api.delete(`/books/${id}`);
      fetchBooks();
      alert('🗑️ تم حذف الكتاب بنجاح');
    } catch (err) {
      console.error(err);
      alert('❌ حدث خطأ أثناء حذف الكتاب');
    }
  };

  const downloadSVG = (id) => {
    const wrapper = document.getElementById(id);
    if (!wrapper) {
      alert(`❌ العنصر غير موجود: ${id}`);
      return;
    }
    // QRCode/Barcode كلاهما يولّد SVG كعنصر ابن
    const svg = wrapper.tagName?.toLowerCase() === 'svg'
      ? wrapper
      : wrapper.querySelector('svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id}.svg`;
    a.click();
  };

  const fmtIQD = (v) => {
    const n = Number(v);
    if (!n) return '—';
    try {
      return new Intl.NumberFormat('ar-IQ', {
        style: 'currency',
        currency: 'IQD',
        maximumFractionDigits: 0
      }).format(n);
    } catch {
      return `${n} د.ع`;
    }
  };

  // ---------- إعداد البيانات للعرض (بحث/تجميع/فرز) ----------
  const normalizeGenre = (g) => (g && String(g).trim()) || 'غير مصنف';

  const { categories, orderedCategoryNames } = useMemo(() => {
    // 1) فلترة بحسب البحث العام على الكتب + فلترة الفئة
    const term = searchTerm.trim().toLowerCase();
    const genreFilter = filterGenre.trim();

    const filteredBooks = books.filter((b) => {
      const matchesTerm =
        !term ||
        String(b.name || '').toLowerCase().includes(term) ||
        String(b.author || '').toLowerCase().includes(term);

      const matchesGenre = !genreFilter || normalizeGenre(b.genre) === genreFilter;

      return matchesTerm && matchesGenre;
    });

    // 2) تجميع حسب الفئة
    const map = new Map(); // genre -> array<book>
    for (const b of filteredBooks) {
      const g = normalizeGenre(b.genre);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(b);
    }

    // 3) بحث داخل أسماء الفئات
    const catTerm = categorySearch.trim().toLowerCase();
    let names = Array.from(map.keys());
    if (catTerm) names = names.filter((g) => g.toLowerCase().includes(catTerm));

    // 4) فرز الفئات
    if (categorySort === 'alpha') {
      names.sort((a, b) => a.localeCompare(b, 'ar', { sensitivity: 'base' }));
    } else if (categorySort === 'count') {
      names.sort((a, b) => map.get(b).length - map.get(a).length || a.localeCompare(b, 'ar'));
    }

    return { categories: map, orderedCategoryNames: names };
  }, [books, searchTerm, filterGenre, categorySearch, categorySort]);

  // 5) فرز الكتب داخل فئة واحدة
  const sortBooksInCategory = (genreName, arr) => {
    const mode = perCategorySort[genreName] || 'alpha';
    const list = [...arr];
    list.sort((a, b) => {
      if (mode === 'alpha') {
        return (a.name || '').localeCompare(b.name || '', 'ar', { sensitivity: 'base' });
      }
      if (mode === 'entryDate') {
        return new Date(a.entryDate) - new Date(b.entryDate);
      }
      return 0;
    });
    return list;
  };

  const allGenres = useMemo(() => {
    const s = new Set(books.map((b) => normalizeGenre(b.genre)));
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'ar'));
  }, [books]);

  return (
    <div dir="rtl" className="container mx-auto max-w-7xl px-4 py-4 space-y-6">
      {/* يجعل SVG الباركود/QR يتناسب مع عرض الحاوية */}
      <style>{`
        .barcode-wrap svg { width: 100% !important; height: auto !important; }
      `}</style>

      <h2 className="text-2xl md:text-3xl font-bold text-blue-800 mb-4 text-center">
        {editId ? '✏️ تعديل كتاب' : '📚 أرشفة الكتب'}
      </h2>

      {/* شريط أدوات العرض */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 bg-white p-4 rounded-lg border shadow-sm">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="🔎 ابحث عن كتاب (اسم/مؤلف)"
          className="border p-2 rounded w-full"
        />

        <select
          value={filterGenre}
          onChange={(e) => setFilterGenre(e.target.value)}
          className="border p-2 rounded w-full"
        >
          <option value="">كل الفئات</option>
          {allGenres.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>

        <input
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
          placeholder="🔎 بحث داخل أسماء الفئات"
          className="border p-2 rounded w-full"
        />

        <select
          value={categorySort}
          onChange={(e) => setCategorySort(e.target.value)}
          className="border p-2 rounded w-full"
        >
          <option value="alpha">فرز الفئات: أبجدي</option>
          <option value="count">فرز الفئات: حسب عدد الكتب</option>
        </select>

        <div className="text-xs text-gray-500 self-center">
          اختر فئة محددة من القائمة لإظهارها فقط.
        </div>
      </div>

      {/* نموذج إضافة/تعديل */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-blue-50 p-6 rounded-lg shadow"
      >
        <input name="name" value={form.name} onChange={handleChange} placeholder="📘 اسم الكتاب" required className="border p-2 rounded w-full" />
        <input name="author" value={form.author} onChange={handleChange} placeholder="✍️ المؤلف" required className="border p-2 rounded w-full" />
        <input name="genre" value={form.genre} onChange={handleChange} placeholder="📂 النوع (الفئة)" className="border p-2 rounded w-full" />

        <input type="date" name="entryDate" value={form.entryDate} onChange={handleChange} required className="border p-2 rounded w-full" />
        <input name="barcode" value={form.barcode} onChange={handleChange} placeholder="🏷️ الباركود (اختياري)" className="border p-2 rounded w-full" />

        {/* رفع الغلاف */}
        <div className="sm:col-span-2 flex flex-col gap-2">
          <label className="text-sm text-gray-700">🖼️ صورة الغلاف (ملف)</label>
          <input
            key={fileInputKey}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="border p-2 rounded w-full"
          />
          {coverPreview ? (
            <img
              src={coverPreview}
              alt="معاينة الغلاف"
              className="w-full h-40 object-cover rounded border"
            />
          ) : null}
          <small className="text-gray-600">الحد الأقصى 100MB. إذا ما تختار ملف، تقدر تكتب رابط مباشر أدناه.</small>
        </div>

        {/* رابط الغلاف */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-700">🔗 رابط صورة الغلاف (اختياري)</label>
          <input
            name="coverUrl"
            value={form.coverUrl}
            onChange={handleChange}
            placeholder="مثال: https://example.com/cover.jpg"
            className="border p-2 rounded w-full"
          />
        </div>

        {/* السعر */}
        <div className="flex flex-col">
          <label htmlFor="price" className="mb-1 text-gray-700 font-medium">
            💰 السعر (د.ع)
          </label>
          <input
            id="price"
            type="number"
            min="0"
            step="500"
            name="price"
            value={form.price}
            onChange={handleChange}
            placeholder="أدخل السعر (د.ع)"
            inputMode="numeric"
            className="border p-2 rounded w-full"
          />
        </div>

        <button
          type="submit"
          disabled={isSaving || isUploading}
          className="sm:col-span-2 lg:col-span-3 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition font-semibold disabled:opacity-60"
        >
          {isSaving ? (editId ? '⏳ تحديث...' : '⏳ إضافة...') : (editId ? '🔄 تحديث' : '➕ إضافة كتاب')}
        </button>
      </form>

      {/* العرض مجمّع بحسب الفئات */}
      <div className="space-y-8">
        {orderedCategoryNames.length === 0 && (
          <div className="text-center text-gray-500">لا توجد نتائج مطابقة.</div>
        )}

        {orderedCategoryNames.map((genreName) => {
          const originalList = categories.get(genreName) || [];
          const list = sortBooksInCategory(genreName, originalList);
          if (!list.length) return null;

          const currentSort = perCategorySort[genreName] || 'alpha';

          return (
            <section key={genreName} className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-xl font-extrabold text-blue-900">
                  {genreName} <span className="text-sm text-gray-500">({list.length})</span>
                </h3>

                {/* فرز داخل الفئة */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">فرز كتب الفئة:</label>
                  <select
                    value={currentSort}
                    onChange={(e) =>
                      setPerCategorySort((prev) => ({ ...prev, [genreName]: e.target.value }))
                    }
                    className="border p-2 rounded text-sm"
                  >
                    <option value="alpha">أبجدي (اسم الكتاب)</option>
                    <option value="entryDate">حسب تاريخ الإضافة</option>
                  </select>
                </div>
              </div>

              {/* كروت الكتب: 1 عمود (موبايل) → 2 (SM) → 3 (MD) → 4 (XL) */}
              <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {list.map((book) => {
                  const cover = book.coverUrl || placeholderCover;
                  const price = book.price ?? 0;
                  const barcodeVal = book.barcode || '';
                  return (
                    <motion.div
                      key={book._id}
                      className="bg-white border rounded-xl p-4 shadow-md hover:shadow-xl transition-all flex flex-col overflow-hidden min-w-0"
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex-1 min-w-0">
                        <img
                          src={cover}
                          alt={`غلاف ${book.name || ''}`}
                          className="w-full h-40 object-cover rounded-lg border mb-3"
                          onError={(e) => { e.currentTarget.src = placeholderCover; }}
                        />
                        <h4 className="font-bold text-lg text-blue-800 truncate">{book.name}</h4>
                        <p className="text-sm text-gray-700 mt-1 truncate break-words">✍️ المؤلف: {book.author || '—'}</p>
                        <p className="text-sm text-gray-700 truncate break-words">📂 الفئة: {normalizeGenre(book.genre)}</p>
                        <p className="text-sm text-gray-700">📅 الإدخال: {book.entryDate ? new Date(book.entryDate).toLocaleDateString() : '—'}</p>
                        <p className="text-sm text-gray-700 mt-1">💰 السعر: <span className="font-semibold">{fmtIQD(price)}</span></p>
                      </div>

                      {/* الأكواد */}
                      <div className="flex justify-between items-end gap-3 mt-4">
                        <div className="text-center flex-1 barcode-wrap" id={`bc-${book._id}`}>
                          {barcodeVal ? (
                            <>
                              <Barcode
                                value={barcodeVal}
                                width={1.8}
                                height={84}
                                fontSize={10}
                                displayValue={true}
                                background="#ffffff"
                                lineColor="#111827"
                                renderer="svg"
                              />
                              <button onClick={() => downloadSVG(`bc-${book._id}`)} className="text-xs text-blue-600 underline mt-1">
                                تحميل
                              </button>
                            </>
                          ) : (
                            <div className="text-xs text-gray-500">لا يوجد باركود</div>
                          )}
                        </div>

                        <div className="text-center" id={`qr-${book._id}`}>
                          <QRCode value={barcodeVal || 'N/A'} size={56} style={{ height: '56px', width: '56px' }} />
                          <button onClick={() => downloadSVG(`qr-${book._id}`)} className="text-xs text-blue-600 underline mt-1">
                            تحميل
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between mt-4">
                        <button
                          onClick={() => handleEdit(book)}
                          className="text-sm text-blue-700 hover:underline"
                        >
                          ✏️ تعديل
                        </button>
                        <button
                          onClick={() => handleDelete(book._id)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          🗑️ حذف
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
